/**
 * HeaderDesktop Component
 *
 * Renders the desktop header for company details page.
 * Includes company logo, name, headquarters, industries, and action buttons
 * arranged in a two-row layout (top: website/linkedin/search, bottom: watchlist/download).
 */

import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import DataSourceToggleIcon from '../../../common/DataSourceToggleIcon';
import HeaderDownloadPopup from './HeaderDownloadPopup';
import { headerActionPropTypes } from './headerActionPropTypes';
import HeaderIndustriesList from './HeaderIndustriesList';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';
import { getLogoUrl, LOGO_PLACEHOLDER } from '../../../../utils/logo';

/**
 * @param {Object} props
 * @param {Object} props.company - Company data with id, name, headquarters, website, industries
 * @param {boolean} props.isToggleFinancialDownload - Whether download popup is toggled open
 * @param {string} props.linkedInTooltipText - Tooltip text for LinkedIn button
 * @param {string} props.searchTooltipText - Tooltip text for search button
 * @param {boolean} props.isSearchDisabled - Whether search button is disabled
 * @param {React.ReactNode} props.watchlistTooltipContent - Tooltip content for watchlist button
 * @param {string} props.watchlistTooltipPosition - Tooltip position for watchlist button
 * @param {boolean} props.watchlistTooltipClickable - Whether watchlist tooltip is clickable
 * @param {boolean} props.isWatchlistDisabled - Whether watchlist button is disabled
 * @param {boolean} props.isInWatchlist - Whether company is in watchlist
 * @param {React.ReactNode} props.downloadTooltipContent - Tooltip content for download button
 * @param {number} props.windowWidth - Current window width for responsive tooltips
 * @param {Object} props.downloadPopupProps - Props to pass to HeaderDownloadPopup
 * @param {Function} props.onWebsiteClick - Handler for website button click
 * @param {Function} props.onLinkedInClick - Handler for LinkedIn button click
 * @param {Function} props.onSearchClick - Handler for search button click
 * @param {Function} props.onWatchlistClick - Handler for watchlist button click
 * @param {Function} props.onDownloadToggle - Handler for download toggle button click
 * @param {Function} props.onDownload - Handler for download action from popup
 * @param {Function} props.requestBulkExport - Handler for bulk-gated download action (comparables)
 */
const HeaderDesktop = ({
  company,
  isToggleFinancialDownload,
  linkedInTooltipText,
  searchTooltipText,
  isSearchDisabled,
  watchlistTooltipContent,
  watchlistTooltipPosition,
  watchlistTooltipClickable,
  isWatchlistDisabled,
  isInWatchlist,
  downloadTooltipContent,
  windowWidth,
  downloadPopupProps,
  onWebsiteClick,
  onLinkedInClick,
  onSearchClick,
  onWatchlistClick,
  onDownloadToggle,
  onDownload,
  requestBulkExport
}) => {
  // Special tooltip positioning for narrow desktop range (1200-1500px)
  const isNarrowDesktop = windowWidth > 1200 && windowWidth < 1500;

  // Compute whether to hide tooltips when download popup is open
  // Exception: in narrow desktop range, tooltips remain visible
  const shouldHideTooltip = isToggleFinancialDownload && !isNarrowDesktop;
  const logoUrl = getLogoUrl(company.website_SAFE_LOGO);

  // Download tooltip: none while the popup is open, otherwise whatever the parent
  // passes. TD-2648 moved the export-disabled right onto the entries inside the
  // popup, so the trigger no longer has a deactivated variant of its own.
  let downloadTooltip = null;
  if (!isToggleFinancialDownload) {
    downloadTooltip = downloadTooltipContent;
  }

  return (
    <div className="company-details-header">
      <img className="logo" src={logoUrl || LOGO_PLACEHOLDER} alt="company logo" />

      <div
        className="info-col company-name"
        style={company.industries?.length ? { maxWidth: '300px' } : {}}
      >
        <div className="bold-text">{company.name || PUNCTUATION.EMPTY_VALUE}</div>
        <div className="normal-text">{company.headquarters || PUNCTUATION.EMPTY_VALUE}</div>
      </div>

      <div className="separator" />

      <div className="info-col industries">
        <div className={`bold-text ${dataSourceClass(FIELD_SOURCE.industries)}`}>Industries</div>
        <HeaderIndustriesList industries={company.industries} emptyClassName="normal-text" />
      </div>

      <div className="separator" />

      <div className="action-btns">
        <div className="top-actions">
          {!!company.website && (
            <IconButton
              id="website-btn"
              type={ICON_BUTTON_TYPES.BLUE}
              icon={ICON_BUTTON_ICONS.GLOBE2}
              tooltipText={shouldHideTooltip ? null : `Visit ${company.website}`}
              onClick={onWebsiteClick}
              {...(isNarrowDesktop ? { tooltipPosition: 'top-center' } : {})}
            />
          )}
          <IconButton
            id="linkedin-btn"
            type={ICON_BUTTON_TYPES.BLUE}
            icon={ICON_BUTTON_ICONS.LINKEDIN}
            tooltipText={shouldHideTooltip ? null : linkedInTooltipText}
            onClick={onLinkedInClick}
            {...(isNarrowDesktop ? { tooltipPosition: 'top-center' } : {})}
          />
          <IconButton
            id="search-from-company-details-btn"
            type={ICON_BUTTON_TYPES.BLUE}
            icon={ICON_BUTTON_ICONS.SEARCH}
            onClick={onSearchClick}
            tooltipText={shouldHideTooltip ? null : searchTooltipText}
            disabled={isSearchDisabled}
            {...(isNarrowDesktop ? { tooltipPosition: 'top-end' } : {})}
          />
        </div>

        <div className="bottom-actions">
          <IconButton
            id="psrd-btn"
            type={ICON_BUTTON_TYPES.BLUE}
            icon={ICON_BUTTON_ICONS.WATCHLIST}
            disabled={isWatchlistDisabled}
            tooltipContent={isToggleFinancialDownload ? null : watchlistTooltipContent}
            tooltipPosition={watchlistTooltipPosition}
            tooltipClickable={watchlistTooltipClickable}
            filled={isInWatchlist}
            onClick={onWatchlistClick}
          />
          <DataSourceToggleIcon />
          <IconButton
            id="psrf-btn"
            type={ICON_BUTTON_TYPES.BLUE}
            icon={ICON_BUTTON_ICONS.DOWNLOAD}
            onClick={onDownloadToggle}
            filled={isToggleFinancialDownload}
            tooltipContent={downloadTooltip}
          />
        </div>

        {isToggleFinancialDownload && (
          <HeaderDownloadPopup
            onDownload={onDownload}
            requestBulkExport={requestBulkExport}
            {...downloadPopupProps}
          />
        )}
      </div>
    </div>
  );
};

HeaderDesktop.propTypes = headerActionPropTypes;

export default HeaderDesktop;
